import { createContext, useContext } from "react";

import type {
  MentionItems,
  MentionKinds,
} from "src/base-components/Chat/PromptBox/mentionContent";

export type ReferenceRendering = {
  kinds?: MentionKinds;
};

const EMPTY_ITEMS: MentionItems = [];
const EMPTY_RENDERING: ReferenceRendering = {};

// Items and rendering config live in separate contexts: items follow page
// state and can change often, while every message bubble subscribes to the
// rendering config — an items update must not invalidate the bubbles.
const ReferenceItemsContext = createContext<MentionItems>(EMPTY_ITEMS);
const ReferenceRenderingContext =
  createContext<ReferenceRendering>(EMPTY_RENDERING);

type Props = {
  /**
   * Entries for the @ menu. Reactive: passing a new array updates the
   * dropdown, even while it is open. Empty or omitted disables mentions.
   */
  items?: MentionItems;
  /**
   * kind → { label, icon }: dropdown group headers and the icon shown with
   * references in the dropdown, the editor, and sent messages.
   */
  kinds?: MentionKinds;
  children: React.ReactNode;
};

/**
 * Single source for a chat's reference (@-mention) config. Wrap the chat
 * once; `PromptBox` and `ChatUserBubble`/`ChatMarkdownRenderer` both read
 * from it, so the editor and the sent messages cannot drift apart.
 */
export const ChatReferencesProvider: React.FC<Props> = ({
  items,
  kinds,
  children,
}) => (
  <ReferenceItemsContext value={items ?? EMPTY_ITEMS}>
    <ReferenceRenderingContext value={{ kinds }}>
      {children}
    </ReferenceRenderingContext>
  </ReferenceItemsContext>
);

/** Returns [] outside a provider, which renders mentions disabled. */
export const useReferenceItems = (): MentionItems =>
  useContext(ReferenceItemsContext);

/** Returns {} outside a provider: default pills, no icons. */
export const useReferenceRendering = (): ReferenceRendering =>
  useContext(ReferenceRenderingContext);
